import { useState, useEffect } from 'react';
import { idsNeedingQuestionnaire } from '@/lib/assessment/questionnaireReminder';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { FileText, Loader2, Edit, Columns3, ArrowUp, ArrowDown, ArrowUpDown, LineChart } from 'lucide-react';
import { ExamReportDialog } from '@/components/health/ExamReportDialog';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { canTakeExam } from '@/lib/firebase/queries/exams';
import { subscribeToCustomersExams } from '@/lib/firebase/queries/exams/subscribeToExams';
import { UnifiedQuestionnaireDialog } from './UnifiedQuestionnaireDialog';
import { loadClinicalSummaries, loadClinicalSummary, type ClinicalSummary } from '@/lib/firebase/queries/namatAssessment';
import { getClinicalColumns, saveClinicalColumns } from '@/lib/firebase/queries/tablePreferences';
import { INDICATORS, type Indicator } from '@/lib/assessment/namatAssessment';
import { DEFAULT_CLINICAL_COLUMNS, toggleClinicalColumn } from '@/lib/tables/clinicalColumns';
import { NamatScaleCell } from '@/components/health/NamatScaleCell';
import { IndicatorBadge } from '@/components/health/IndicatorBadge';
import { indicatorUnit } from '@/components/health/indicatorUnit';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { getOrganizationMembers } from '@/lib/firebase/queries/organizations';
import { getDocsByIds } from '@/lib/firebase/queries/batchedIn';
import { maskPhone } from '@/lib/privacy/maskPhone';
import { isActiveBeneficiary } from '@/lib/competitions/competitionRanking';
import {
  matchesExamineeSearch,
  sortExaminees,
  nextExamineeSort,
  type ExamineeSort,
  type ExamineeSortKey,
} from '@/lib/examiner/examineeSearch';

interface CustomerWithExamStatus {
  id: string;
  firstNameEn: string;
  lastNameEn: string;
  firstNameAr: string;
  lastNameAr: string;
  phone: string;
  email: string;
  organizationId: string;
  lastExam?: Date | null;
  lastExamStatus: 'pending' | 'completed' | 'in_progress';
  questionnaireCompleted?: boolean;
  /** Namat scale and the latest exam's indicators (#93). */
  summary?: ClinicalSummary;
}

interface ExamTableProps {
  searchQuery: string;
  examStatusFilter: string | null;
  questionnaireStatusFilter: string | null;
  organizationId: string;
  onExamClick: (customer: CustomerWithExamStatus) => void;
  /**
   * #97: the beneficiaries matching the search and exam filters whose
   * questionnaire is not valid — the count on the «غير مكتمل» filter and
   * who the reminder goes to.
   */
  onNotFilledChange?: (userIds: string[]) => void;
}

export function ExamTable({ searchQuery, examStatusFilter, questionnaireStatusFilter, organizationId, onExamClick, onNotFilledChange }: ExamTableProps) {
  const [customers, setCustomers] = useState<CustomerWithExamStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingCustomerId, setProcessingCustomerId] = useState<string | null>(null);
  const [selectedCustomerForQuestionnaire, setSelectedCustomerForQuestionnaire] = useState<CustomerWithExamStatus | null>(null);
  /** #112: whose exam report is open. */
  const [reportCustomer, setReportCustomer] = useState<CustomerWithExamStatus | null>(null);
  // No mode needed - always directly editable
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();
  // The indicator columns this user chose; kept on their profile (#93).
  const [columns, setColumns] = useState<Indicator[]>(DEFAULT_CLINICAL_COLUMNS);
  // #63: clickable name / phone headers; null keeps the load order.
  const [sort, setSort] = useState<ExamineeSort | null>(null);

  useEffect(() => {
    if (!currentUser?.uid) return;
    getClinicalColumns(currentUser.uid).then(setColumns).catch(() => {});
  }, [currentUser?.uid]);

  const toggleColumn = (key: Indicator) => {
    const next = toggleClinicalColumn(columns, key);
    setColumns(next);
    if (currentUser?.uid) {
      saveClinicalColumns(currentUser.uid, next).catch(() => toast.error(t('dashboard.examiner.customers.columns.saveError')));
    }
  };

  useEffect(() => {
    let unsubscribes: (() => void)[] = [];

    const fetchCustomers = async () => {
      try {
        setLoading(true);
        let customerData: CustomerWithExamStatus[];

        if (organizationId) {
          // Fetch customers from the selected organization
          const members = await getOrganizationMembers(organizationId);
          const customerMembers = members.filter(m => m.role === 'customer');
          
          // Fetch user details for every customer member in batched reads
          // (30 per query, #117). Show anyone with customer role, even if
          // they have other roles.
          const userDocs = await getDocsByIds('users', customerMembers.map(m => m.userId));
          const users = customerMembers
            .map((member) => ({ member, userData: userDocs.get(member.userId) }))
            // Deleted or deactivated accounts are not beneficiaries (namat#87).
            .filter(({ userData }) => isActiveBeneficiary(userData));

          // Namat scale and the latest exam's indicators (#93), for everyone
          // at once in batched reads (#73). A user missing from the map is
          // one whose chunk failed: no scale shown, not counted as not filled.
          const summaries = await loadClinicalSummaries(users.map(({ member }) => member.userId));

          customerData = users.map(({ member, userData }) => {
            const summary = summaries.get(member.userId);
            return {
              id: member.userId,
              firstNameEn: userData?.firstNameEn || '',
              lastNameEn: userData?.lastNameEn || '',
              firstNameAr: userData?.firstNameAr || '',
              lastNameAr: userData?.lastNameAr || '',
              phone: userData?.phone || '',
              email: userData?.email || '',
              organizationId: member.organizationId,
              lastExam: null,
              lastExamStatus: 'pending' as const,
              questionnaireCompleted: summary?.assessment.status === 'valid',
              summary
            };
          });
        } else {
          // No organization selected, show empty list for examiners
          customerData = [];
        }

        setCustomers(customerData);
        
        // Live exam updates, one listener per 30 customers (#117): the row
        // flips to «edit exam» as soon as the examiner saves.
        unsubscribes = [
          subscribeToCustomersExams(
            customerData.map(customer => customer.id),
            (updates) => {
              setCustomers(prev => prev.map(c => {
                const update = updates.get(c.id);
                return update ? {
                  ...c,
                  lastExam: update.lastExam ? new Date(update.lastExam) : null,
                  lastExamStatus: (update.lastExamStatus || 'pending') as 'pending' | 'completed' | 'in_progress'
                } : c;
              }));
            },
            (error) => {
              console.error('Error subscribing to customer exams:', error);
            }
          )
        ];
      } catch (error) {
        console.error('Error fetching customers:', error);
        toast.error(t('dashboard.examiner.customers.errors.fetchFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [organizationId, t]);

  const handleExamClick = async (customer: CustomerWithExamStatus) => {
    try {
      setProcessingCustomerId(customer.id);
      const { allowed, existingExam } = await canTakeExam(customer.id);
      
      // If not allowed and there's no existing exam, show error
      if (!allowed && !existingExam) {
        toast.error(t('dashboard.examiner.customers.errors.alreadyExamined'));
        return;
      }
      
      // If allowed or there's an existing exam, proceed
      onExamClick(customer);
    } catch (error) {
      console.error('Error checking exam eligibility:', error);
      toast.error(t('dashboard.examiner.customers.errors.checkEligibilityFailed'));
    } finally {
      setProcessingCustomerId(null);
    }
  };

  const matchesSearchAndExam = (customer: CustomerWithExamStatus) => {
    // #63: Arabic / English name, email, or phone digits.
    const matchesSearch = matchesExamineeSearch(customer, searchQuery);
    
    // Check exam status filter
    let matchesExamStatus = true;
    if (examStatusFilter) {
      switch (examStatusFilter) {
        case 'exam-completed':
          matchesExamStatus = !!customer.lastExam;
          break;
        case 'exam-not-completed':
          matchesExamStatus = !customer.lastExam;
          break;
        default:
          matchesExamStatus = true;
      }
    }

    const matchesOrganization = !organizationId || customer.organizationId === organizationId;
    return matchesSearch && matchesExamStatus && matchesOrganization;
  };

  const notFilledIds = idsNeedingQuestionnaire(
    customers.filter(matchesSearchAndExam),
    (c) => c.id,
    (c) => c.summary?.assessment,
  );
  const notFilledKey = notFilledIds.join(',');
  useEffect(() => {
    onNotFilledChange?.(notFilledKey ? notFilledKey.split(',') : []);
    // The key carries the ids; a new array each render must not re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notFilledKey]);

  const matchingCustomers = customers.filter(customer => {
    if (!matchesSearchAndExam(customer)) return false;

    // Check questionnaire status filter
    let matchesQuestionnaireStatus = true;
    if (questionnaireStatusFilter) {
      switch (questionnaireStatusFilter) {
        case 'questionnaire-completed':
          matchesQuestionnaireStatus = !!customer.questionnaireCompleted;
          break;
        case 'questionnaire-not-completed':
          matchesQuestionnaireStatus = !customer.questionnaireCompleted;
          break;
        default:
          matchesQuestionnaireStatus = true;
      }
    }

    return matchesQuestionnaireStatus;
  });
  const filteredCustomers = sortExaminees(matchingCustomers, sort, dir === 'rtl');

  const sortableHead = (key: ExamineeSortKey, label: string) => {
    const active = sort?.key === key;
    const Icon = !active ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown;
    return (
      <TableHead
        className="text-right"
        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          onClick={() => setSort((s) => nextExamineeSort(s, key))}
          className="inline-flex items-center gap-1 hover:text-foreground"
          data-testid={`sort-${key}`}
        >
          {label}
          <Icon className={`h-3.5 w-3.5 ${active ? '' : 'opacity-40'}`} />
        </button>
      </TableHead>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-2" dir={dir}>
      <div className="flex justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="columns-chooser">
              <Columns3 className="h-4 w-4" />
              {t('dashboard.examiner.customers.columns.title')}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>{t('dashboard.examiner.customers.columns.indicators')}</DropdownMenuLabel>
            {INDICATORS.map((key) => (
              <DropdownMenuCheckboxItem
                key={key}
                checked={columns.includes(key)}
                onCheckedChange={() => toggleColumn(key)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(`common.health.indicators.${key}`)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {sortableHead('name', t('dashboard.examiner.customers.table.name'))}
            {sortableHead('phone', t('dashboard.examiner.customers.table.phone'))}
            <TableHead className="text-right">{t('dashboard.examiner.customers.table.scale')}</TableHead>
            {columns.map((key) => (
              <TableHead key={key} className="text-right whitespace-nowrap">
                {t(`common.health.indicators.${key}`)}
              </TableHead>
            ))}
            <TableHead className="text-right">{t('dashboard.examiner.customers.table.questionnaire') || 'Questionnaire'}</TableHead>
            <TableHead className="text-right">{t('dashboard.examiner.customers.table.exams') || 'Exams'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredCustomers.map((customer) => (
            <TableRow key={customer.id}>
              <TableCell className="font-medium text-right">
                {dir === 'rtl'
                  ? `${customer.firstNameAr} ${customer.lastNameAr}`
                  : `${customer.firstNameEn} ${customer.lastNameEn}`}
              </TableCell>
              <TableCell className="text-right">
                {/* Masked for privacy: the list identifies patients, it is not a call sheet. */}
                <span dir="ltr">{maskPhone(customer.phone)}</span>
              </TableCell>
              <TableCell className="text-right">
                <NamatScaleCell assessment={customer.summary?.assessment} />
              </TableCell>
              {columns.map((key) => {
                const reading = customer.summary?.indicators.find((r) => r.key === key);
                return (
                  <TableCell key={key} className="text-right whitespace-nowrap" data-testid={`cell-${key}`}>
                    {reading ? (
                      <div className="space-y-1">
                        <div dir="ltr" className="text-sm text-end">
                          {reading.value} <span className="text-xs text-muted-foreground">{indicatorUnit(reading, t)}</span>
                        </div>
                        <IndicatorBadge reading={reading} t={t} className="justify-end" />
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                );
              })}
              <TableCell className="text-right">
                <Button
                  variant={customer.questionnaireCompleted ? "outline" : "default"}
                  size="sm"
                  onClick={() => setSelectedCustomerForQuestionnaire(customer)}
                  className={customer.questionnaireCompleted ? "hover:bg-[#004250] hover:text-white" : "bg-[#004250] hover:bg-[#003240] text-white"}
                >
                  {customer.questionnaireCompleted ? (
                    <>
                      <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                      {t('dashboard.examiner.customers.questionnaire.editAnswers') || 'Edit Questionnaire'}
                    </>
                  ) : (
                    <>
                      <FileText className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                      {t('dashboard.examiner.customers.questionnaire.fillQuestionnaire') || 'Fill Questionnaire'}
                    </>
                  )}
                </Button>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant={customer.lastExam ? "outline" : "default"}
                  size="sm"
                  onClick={() => handleExamClick(customer)}
                  disabled={!!processingCustomerId}
                  className={customer.lastExam ? "hover:bg-[#7dda55] hover:text-white" : "bg-[#7dda55] hover:bg-[#6dc447] text-white"}
                >
                  {processingCustomerId === customer.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : customer.lastExam ? (
                    <>
                      <Edit className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                      {t('dashboard.examiner.customers.actions.editExam')}
                    </>
                  ) : (
                    <>
                      <FileText className={`h-4 w-4 ${dir === 'rtl' ? 'ml-3' : 'mr-2'}`} />
                      {t('dashboard.examiner.customers.actions.recordExam')}
                    </>
                  )}
                </Button>
                {customer.lastExam && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReportCustomer(customer)}
                    aria-label={t('common.health.report.openFor')}
                    data-testid={`exam-report-button-${customer.id}`}
                    className="ms-1"
                  >
                    <LineChart className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} aria-hidden />
                    {t('common.health.report.button')}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {filteredCustomers.length === 0 && (
            <TableRow>
              <TableCell colSpan={5 + columns.length} className="text-center">
                {t('dashboard.examiner.customers.table.noCustomers')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>

      {/* Exam report over time (#112) */}
      {reportCustomer && (
        <ExamReportDialog
          open={!!reportCustomer}
          onOpenChange={(open) => !open && setReportCustomer(null)}
          customerId={reportCustomer.id}
          customerName={
            dir === 'rtl'
              ? `${reportCustomer.firstNameAr} ${reportCustomer.lastNameAr}`
              : `${reportCustomer.firstNameEn} ${reportCustomer.lastNameEn}`
          }
        />
      )}

      {/* Unified Questionnaire Dialog */}
      <UnifiedQuestionnaireDialog
        open={!!selectedCustomerForQuestionnaire}
        onOpenChange={(open) => !open && setSelectedCustomerForQuestionnaire(null)}
        customerId={selectedCustomerForQuestionnaire?.id || ''}
        customerName={selectedCustomerForQuestionnaire ? (
          dir === 'rtl'
            ? `${selectedCustomerForQuestionnaire.firstNameAr} ${selectedCustomerForQuestionnaire.lastNameAr}`
            : `${selectedCustomerForQuestionnaire.firstNameEn} ${selectedCustomerForQuestionnaire.lastNameEn}`
        ) : ''}

        onSuccess={() => {
          // Refresh questionnaire completion status
          if (selectedCustomerForQuestionnaire) {
            loadClinicalSummary(selectedCustomerForQuestionnaire.id).then(summary => {
              setCustomers(prev => prev.map(c =>
                c.id === selectedCustomerForQuestionnaire.id
                  ? { ...c, questionnaireCompleted: summary.assessment.status === 'valid', summary }
                  : c
              ));
            });
          }
        }}
      />
    </div>
  );
}